import { FC, ReactNode } from 'react';
import type { FrescorReplica } from '@/types/dashboards';
import { fmtData, fmtDataHora, fmtUn } from '@/utils/painelFormat';
import Aviso from '../Aviso';
import { STATUS } from '../chartTheme';

interface Props {
  frescor: FrescorReplica;
  /** Lotes na fila com entrada antes do período. */
  antigos: number;
  antigosReprocesso: number;
  maisAntigo: string | null;
  inicioJanela: string;
  /** Fichas pendentes com o lote fora do CQ (fora da fila). */
  semSaldo?: number;
  /** Lotes com saldo no CQ sem ficha na cópia. */
  saldoSemFicha?: { lotes: number; quantidade: number };
  /** A cópia do saldo do CQ (grupo complementar da réplica). */
  saldoCq?: { atualizado_em: string | null; atrasado: boolean };
}

function avisoReplica(frescor: FrescorReplica): ReactNode {
  if (!frescor.replica_atrasada) return null;
  return (
    <Aviso key="frescor" testId="aviso-replica" icone="cloud_off" cor={STATUS.critico} titulo="Réplica do Datasul parada">
      A última carga foi em {fmtDataHora(frescor.ultima_carga_replica)}
      {frescor.dias_sem_carga !== null && <> ({frescor.dias_sem_carga} dias)</>}. O tempo de espera dos lotes é contado até{' '}
      {fmtData(frescor.producao_ate)}: o que foi liberado depois disso ainda aparece na fila.
    </Aviso>
  );
}

function avisoSaldo(saldoCq: Props['saldoCq']): ReactNode {
  if (!saldoCq?.atrasado) return null;
  return (
    <Aviso key="saldo" testId="aviso-saldo-cq" icone="inventory" cor={STATUS.atencao} titulo="Saldo do CQ desatualizado">
      A fila sai do saldo do depósito do CQ, e a última cópia dele é de {fmtDataHora(saldoCq.atualizado_em)}. Lote liberado depois
      disso ainda aparece aguardando.
    </Aviso>
  );
}

function avisoAntigos({ antigos, antigosReprocesso, maisAntigo, inicioJanela }: Props): ReactNode {
  if (antigos <= 0) return null;
  return (
    <Aviso key="antigos" testId="aviso-fichas-antigas" icone="history" cor={STATUS.atencao} titulo="Lotes no CQ há muito tempo">
      {fmtUn(antigos)} {antigos === 1 ? 'lote tem' : 'lotes têm'} saldo no CQ com entrada antes de {fmtData(inicioJanela)}
      {maisAntigo && <> — o mais antigo em {fmtData(maisAntigo)}</>}
      {antigosReprocesso > 0 && <>; {fmtUn(antigosReprocesso)} de OP de reprocesso</>}. O estoque está de fato no depósito do CQ:
      vale conferir com a Qualidade o que segura esses lotes.
    </Aviso>
  );
}

function avisoSemSaldo(semSaldo: number): ReactNode {
  if (semSaldo <= 0) return null;
  return (
    <Aviso key="sem-saldo" testId="aviso-fichas-sem-saldo" icone="inventory" cor={STATUS.atencao} titulo="Fichas abertas sem saldo no CQ">
      {fmtUn(semSaldo)} {semSaldo === 1 ? 'ficha está pendente' : 'fichas estão pendentes'} no Datasul com o lote já fora do depósito
      do CQ (reprocesso, OP estornada). Não contam na fila; a lista está abaixo para a Qualidade encerrar.
    </Aviso>
  );
}

function avisoSemFicha(saldoSemFicha: Props['saldoSemFicha']): ReactNode {
  if (!saldoSemFicha || saldoSemFicha.lotes <= 0) return null;
  return (
    <Aviso key="sem-ficha" testId="aviso-saldo-sem-ficha" icone="help" cor={STATUS.atencao} titulo="Saldo no CQ sem ficha na cópia">
      {fmtUn(saldoSemFicha.lotes)} {saldoSemFicha.lotes === 1 ? 'lote tem' : 'lotes têm'} saldo no CQ ({fmtUn(saldoSemFicha.quantidade)} un)
      sem ficha de CQ de produção na cópia. Ficam fora da contagem acima; costuma ser ficha lançada depois da cópia da madrugada.
    </Aviso>
  );
}

/** Réplica parada, saldo do CQ velho, lotes retidos há muito tempo e fichas a encerrar. */
const AvisosQualidade: FC<Props> = (props) => {
  const avisos = [
    avisoReplica(props.frescor),
    avisoSaldo(props.saldoCq),
    avisoAntigos(props),
    avisoSemSaldo(props.semSaldo ?? 0),
    avisoSemFicha(props.saldoSemFicha),
  ].filter(Boolean);
  if (avisos.length === 0) return null;
  return <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">{avisos}</div>;
};

export default AvisosQualidade;
